<template>
  <v-container class="pa-0">
    <v-app-bar color="transparent" class="elevation-0 pa-0 page-header-toolbar">
      <page-header-title :title="page_title"></page-header-title>

      <v-spacer></v-spacer>

      <action-button type="back" @click="$router.go(-1)" />

      <router-link
        :to="{ path: BACKEND_URL + '/certificates/edit/' + certificate.id, props: { id: certificate.id } }"
        v-if="$can('certificate-edit')"
      >
        <action-button type="edit" />
      </router-link>

      <action-button type="delete" @click="del(certificate.id)" v-if="$can('certificate-delete')" />
    </v-app-bar>

    <v-form ref="form">
      <v-container grid-list-md>
        <v-card>
          <v-card-text class="pa-4">
            <v-layout row wrap>
              <v-flex lg3 md4 sm6 xs12>
                <v-text-field
                  name="certificate.title"
                  v-model="certificate.title"
                  :label="$t('certificates.title')"
                  readonly
                  autocomplete="new-password"
                ></v-text-field>
              </v-flex>
              <v-flex lg3 md4 sm6 xs12>
                <v-text-field
                  name="certificate.company_name"
                  v-model="certificate.company_name"
                  :label="$t('certificates.company_name')"
                  readonly
                  autocomplete="new-password"
                ></v-text-field>
              </v-flex>
              <v-flex lg3 md4 sm6 xs12>
                <v-text-field
                  name="certificate.issued_date"
                  :value="computedIssuedDate"
                  :label="$t('certificates.issued_date')"
                  readonly
                  autocomplete="new-password"
                ></v-text-field>
              </v-flex>
              <v-flex lg3 md4 sm6 xs12>
                <v-text-field
                  name="certificate.issued_by"
                  v-model="certificate.issued_by"
                  :label="$t('certificates.issued_by')"
                  readonly
                  autocomplete="new-password"
                ></v-text-field>
              </v-flex>
              <v-flex lg3 md4 sm6 xs12>
                <v-text-field
                  name="certificate.course_name"
                  v-model="certificate.course_name"
                  :label="$t('certificates.course_name')"
                  readonly
                  autocomplete="new-password"
                ></v-text-field>
              </v-flex>
              <v-flex lg3 md4 sm6 xs12>
                <v-text-field
                  name="certificate.course_held_by"
                  v-model="certificate.course_held_by"
                  :label="$t('certificates.course_held_by')"
                  readonly
                  autocomplete="new-password"
                ></v-text-field>
              </v-flex>
              <v-flex lg3 md4 sm6 xs12>
                <v-text-field
                  name="certificate.participant_id"
                  :value="participant ? participant.full_name : ''"
                  :label="$t('certificates.participant_id')"
                  readonly
                  autocomplete="new-password"
                ></v-text-field>
              </v-flex>
              <v-flex lg3 md4 sm6 xs12>
                <v-text-field
                  name="certificate.class_id"
                  :value="classes ? classes.name : ''"
                  :label="$t('certificates.class_id')"
                  readonly
                  autocomplete="new-password"
                ></v-text-field>
              </v-flex>
            </v-layout>
          </v-card-text>
        </v-card>

        <v-card class="mt-2">
          <v-app-bar dense text color="secondary">
            <v-toolbar-title class="subheading">{{ $t("certificates.documents") }}</v-toolbar-title>

            <v-spacer></v-spacer>

            <action-button type="new" small :icon_only="true" @click="showUploadForm = true" />
          </v-app-bar>
          <v-card-text class="pa-4">
            <v-layout row wrap>
              <v-flex md12>
                <v-data-table
                  :headers="dc_headers"
                  :items="documents"
                  :items-per-page="documents.length"
                  :options.sync="dc_pagination"
                  :footer-props="{
                    'items-per-page-options': this.DATATABLE_ITEM_PER_PAGE_OPTIONS,
                    'items-per-page-all-text': $t('global.items_per_page_all_text'),
                    'items-per-page-text': $t('global.items_per_page_text'),
                    'page-text': $t('global.page_text')
                  }"
                  :server-items-length="dc_total"
                  :loading="dc_show_loading"
                  :loading-text="$t('global.loading_text')"
                  :no-data-text="$t('global.no_data_text')"
                  :no-results-text="$t('global.no_results_text')"
                  :must-sort="true"
                >
                  <template v-slot:item.size="{ item }">
                    {{ $formatBytes(item.size) }}
                  </template>

                  <template v-slot:item.actions="{ item }">
                    <action-button type="download" @click="downloadFile(item.id, item.original_filename)" :icon_only="true" />
                    <action-button type="delete" @click="deleteFile(item.id)" :icon_only="true" />
                  </template>
                </v-data-table>
              </v-flex>
            </v-layout>
          </v-card-text>
        </v-card>

        <v-card class="mt-2">
          <v-app-bar dense text color="secondary">
            <v-toolbar-title class="subheading">{{ $t("coursetypes.title") }}</v-toolbar-title>
          </v-app-bar>
          <v-card-text class="pa-4">
            <v-layout row wrap>
              <v-flex md12>
                <v-data-table
                  :headers="ct_headers"
                  :items="coursetypes"
                  :items-per-page="coursetypes.length"
                  :options.sync="ct_pagination"
                  :footer-props="{
                    'items-per-page-options': this.DATATABLE_ITEM_PER_PAGE_OPTIONS,
                    'items-per-page-all-text': $t('global.items_per_page_all_text'),
                    'items-per-page-text': $t('global.items_per_page_text'),
                    'page-text': $t('global.page_text')
                  }"
                  :server-items-length="ct_total"
                  :loading="ct_show_loading"
                  :loading-text="$t('global.loading_text')"
                  :no-data-text="$t('global.no_data_text')"
                  :no-results-text="$t('global.no_results_text')"
                  :must-sort="true"
                >
                  <template v-slot:item.status="{ item }">
                    {{ $dd_course_types_status(item.status) }}
                  </template>
                </v-data-table>
              </v-flex>
            </v-layout>
          </v-card-text>
        </v-card>

        <v-dialog v-model="showUploadForm" persistent max-width="960px">
          <div class="form-modal pa-4">
            <v-container class="pa-0">
              <v-form ref="form">
                <v-container grid-list-md>
                  <v-card>
                    <v-app-bar flat dense color="secondary">
                      <v-toolbar-title class="subheading">{{ $t("certificates.upload_files") }}</v-toolbar-title>
                    </v-app-bar>

                    <v-card-text class="pa-4">
                      <v-flex xs12>
                        <v-file-input
                          v-model="files"
                          color="primary"
                          counter
                          multiple
                          :placeholder="$t('certificates.select_files')"
                          prepend-icon="attach_file"
                          outlined
                          :show-size="1000"
                        >
                          <template v-slot:selection="{ index, text }">
                            <v-chip color="primary" dark label small>
                              {{ text }}
                            </v-chip>
                          </template>
                        </v-file-input>
                      </v-flex>
                      <v-flex xs12>
                        <v-card
                          @drop.prevent="documentOnDrop($event)"
                          @dragover.prevent="document_dragover = true"
                          @dragenter.prevent="document_dragover = true"
                          @dragleave.prevent="document_dragover = false"
                          :class="{ 'grey lighten-2': document_dragover }"
                        >
                          <v-card-text>
                            <v-row class="d-flex flex-column" dense align="center" justify="center">
                              <v-icon :class="[document_dragover ? 'mt-2, mb-6' : 'mt-5']" size="60"> mdi-cloud-upload </v-icon>
                              <p>{{ $t("global.drop_files_here") }}</p>
                            </v-row>
                          </v-card-text>
                        </v-card>
                      </v-flex>
                    </v-card-text>
                    <v-card-actions>
                      <v-spacer />

                      <action-button type="cancel" @click="cancelUpload" />
                      <action-button @click="uploadFile" type="upload" />
                    </v-card-actions>
                  </v-card>
                </v-container>
              </v-form>
            </v-container>
          </div>
        </v-dialog>
      </v-container>
    </v-form>
  </v-container>
</template>

<script>
export default {
  name: "CertificateView",

  props: ["id"],

  data() {
    return {
      page_title: "",

      certificate: {},
      participant: {},
      classes: {},

      files: [],
      document_dragover: false,

      coursetypes: [],
      documents: [],

      ct_ids: [],
      ct_total: 0,
      ct_show_loading: false,
      ct_refresh: false,
      ct_pagination: {
        itemsPerPage: this.$getSetting("rows_per_page"),
        sortBy: ["id"]
      },
      ct_headers: [
        { text: this.$t("coursetypes.name"), value: "name" },
        { text: this.$t("coursetypes.category"), value: "category" },
        { text: this.$t("coursetypes.status"), value: "status" }
      ],

      dc_ids: [],
      dc_total: 0,
      dc_show_loading: false,
      dc_refresh: false,
      dc_pagination: {
        itemsPerPage: this.$getSetting("rows_per_page"),
        sortBy: ["id"]
      },
      dc_headers: [
        { text: this.$t("certificates.filename"), value: "original_filename" },
        { text: this.$t("certificates.mime"), value: "mime" },
        { text: this.$t("certificates.size"), value: "size" },
        { text: this.$t("global.actions"), value: "actions", width: "20%" }
      ],

      showUploadForm: false
    }
  },
  watch: {
    ct_refresh: {
      handler() {
        if (this.ct_refresh) {
          this.ct_list()
        }
      }
    },
    ct_pagination: {
      handler(newVal, oldVal) {
        if (!_.isEqual(newVal, oldVal)) this.ct_refresh = true
      }
    },
    dc_refresh: {
      handler() {
        if (this.dc_refresh) {
          this.dc_list()
        }
      }
    },
    dc_pagination: {
      handler(newVal, oldVal) {
        if (!_.isEqual(newVal, oldVal)) this.dc_refresh = true
      }
    }
  },

  computed: {
    computedIssuedDate() {
      return this.certificate.issued_date ? this.moment.utc(this.certificate.issued_date).format(this.DATE_FORMAT) : ""
    }
  },

  created() {
    axios
      .get(this.API_URL + "certificate/" + this.id)
      .then((res) => {
        this.certificate = res.data.data
        this.participant = this.certificate.participant
        this.classes = this.certificate.class

        this.ct_ids = this.certificate.coursetypes.map((a) => a.id)
        this.dc_ids = this.certificate.documents.map((a) => a.id)

        this.page_title = this.$t("certificates.view_certificate", { name: this.certificate.title })

        this.ct_list()
        this.dc_list()
      })
      .catch((err) => console.log(err))
  },

  methods: {
    ct_list() {
      this.ct_show_loading = true

      let ids = this.ct_ids.join(",")
      let { sortBy, descending, page, itemsPerPage } = this.ct_pagination
      let query = this.$queryString(this.ct_pagination, { id: ids ? ids : null })

      axios
        .get(this.API_URL + "coursetypes" + "?" + query)
        .then((res) => {
          this.coursetypes = res.data.data
          this.ct_total = res.data.meta.total
          this.ct_refresh = false
          this.ct_show_loading = false

          this.ct_pagination.page = res.data.meta.current_page
          this.ct_pagination.itemsPerPage = Number(res.data.meta.per_page)
        })
        .catch((err) => console.log(err))
    },
    dc_list() {
      this.dc_show_loading = true

      let ids = this.dc_ids.join(",")
      let { sortBy, descending, page, itemsPerPage } = this.dc_pagination
      let query = this.$queryString(this.dc_pagination, { id: ids ? ids : null })

      axios
        .get(this.API_URL + "certificate/documents" + "?" + query)
        .then((res) => {
          this.documents = res.data.data
          this.dc_total = res.data.meta.total
          this.dc_refresh = false
          this.dc_show_loading = false

          this.dc_pagination.page = res.data.meta.current_page
          this.dc_pagination.itemsPerPage = Number(res.data.meta.per_page)
        })
        .catch((err) => console.log(err))
    },
    downloadFile(id, filename) {
      axios({
        url: this.API_URL + "certificate/download/",
        method: "POST",
        responseType: "arraybuffer",
        data: {
          id: id
        }
      })
        .then((response) => {
          if (this.$arrayBufferValidation(response.data)) {
            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement("a")
            link.href = url
            link.setAttribute("download", filename)
            link.click()
          }
        })
        .catch((error) => {
          console.log(error.response.data)
        })
    },
    uploadFile() {
      let vm = this
      let formData = new FormData()

      formData.append("id", this.certificate.id)

      this.files.forEach((file, index) => {
        formData.append("files[" + index + "]", file)
      })

      axios
        .post(this.API_URL + "certificate/upload", formData, {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        })
        .then((res) => {
          this.certificate = res.data.data
          this.dc_ids = this.certificate.documents.map((a) => a.id)

          vm.cancelUpload()
          vm.dc_list()
        })
        .catch((err) => console.log(err))
    },
    documentOnDrop(e) {
      this.document_dragover = false
      if (e.dataTransfer.files.length) {
        Array.from(e.dataTransfer.files).forEach((file) => {
          this.files.push(file)
        })
      }
    },
    deleteFile(id) {
      let vm = this

      this.$awn.confirm(
        this.$t("global.delete_message"),
        () => {
          return
        },
        () => {
          axios
            .delete(this.API_URL + "certificate/delete/" + id)
            .then((res) => {
              this.certificate = res.data.data
              this.dc_ids = this.certificate.documents.map((a) => a.id)

              vm.dc_list()
              vm.$awn.success(vm.$t("global.delete_success"))
            })
            .catch((error) => {
              console.log(error.response.data)
            })
        },
        {
          labels: {
            confirm: "",
            confirmCancel: this.$t("global.yes"),
            confirmOk: this.$t("global.no")
          }
        }
      )
    },
    cancelUpload() {
      this.showUploadForm = false
      this.files = []
    },
    del(id) {
      let vm = this

      this.$awn.confirm(
        this.$t("global.delete_message"),
        () => {
          return
        },
        () => {
          axios
            .delete(this.API_URL + "certificate/" + id)
            .then((res) => {
              vm.$router.push(this.BACKEND_URL + "/certificates")
              vm.$awn.success(vm.$t("global.delete_success"))
            })
            .catch((err) => console.log(err))
        },
        {
          labels: {
            confirm: "",
            confirmCancel: this.$t("global.yes"),
            confirmOk: this.$t("global.no")
          }
        }
      )
    }
  }
}
</script>

<style scoped></style>
